import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { 
  BriefcaseIcon, 
  FileTextIcon, 
  UsersIcon, 
  GraduationCapIcon, 
  HomeIcon, 
  HeartIcon,
  AwardIcon,
  HelpCircleIcon,
  MonitorIcon,
  CalendarIcon
} from "lucide-react";

const programs = [
  {
    id: "career-placement",
    title: "Career Placement",
    icon: <div className="p-3 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 shadow-md"><BriefcaseIcon className="h-8 w-8 text-white" /></div>,
    description: "We match your skills and career goals with employers committed to providing second chances. Our team advocates on your behalf and helps prepare you for interviews and workplace success.",
    features: [
      "Personalized job matching based on skills and interests",
      "Pre-screened employment opportunities with supportive employers",
      "Interview preparation and coaching",
      "Post-placement support for ongoing success",
      "Employer mediation and advocacy when needed"
    ],
    color: "bg-primary-50"
  },
  {
    id: "resume-coaching",
    title: "Resume Coaching & Job Readiness",
    icon: <div className="p-3 rounded-full bg-gradient-to-r from-blue-400 to-indigo-600 shadow-md"><FileTextIcon className="h-8 w-8 text-white" /></div>,
    description: "Our expert coaches help you develop a professional resume, practice interview skills, and learn workplace etiquette. We focus on highlighting your strengths and preparing you for job market success.",
    features: [
      "Professional resume development and tailoring",
      "Mock interviews with constructive feedback",
      "Workplace communication and etiquette training",
      "Digital literacy and job application skills",
      "Professional attire assistance and guidance"
    ],
    color: "bg-white"
  },
  {
    id: "case-worker-mentorship",
    title: "1-on-1 Case Worker Mentorship",
    icon: <div className="p-3 rounded-full bg-gradient-to-r from-green-400 to-emerald-600 shadow-md"><UsersIcon className="h-8 w-8 text-white" /></div>,
    description: "You'll work with a dedicated case worker who provides personalized guidance and support throughout your journey. They coordinate services, track your progress, and help overcome obstacles.",
    features: [
      "Regular one-on-one meetings with your case worker",
      "Personalized action plans tailored to your needs",
      "Connection to community resources and services",
      "Emotional support and motivational coaching",
      "Advocacy within the employment and social service systems"
    ],
    color: "bg-primary-50"
  },
  {
    id: "education-support",
    title: "Education & GED Support",
    icon: <div className="p-3 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 shadow-md"><GraduationCapIcon className="h-8 w-8 text-white" /></div>,
    description: "We provide GED preparation, skills training, and connections to educational opportunities. Our education specialists help you navigate financial aid and develop a learning plan tailored to your goals.",
    features: [
      "GED preparation classes and testing assistance",
      "Connection to vocational and certificate programs",
      "Financial aid application support",
      "Basic literacy and math skills tutoring",
      "Computer literacy and digital skills training"
    ],
    color: "bg-white"
  },
  {
    id: "housing-stability",
    title: "Housing & Stability Partnerships",
    icon: <div className="p-3 rounded-full bg-gradient-to-r from-red-400 to-rose-600 shadow-md"><HomeIcon className="h-8 w-8 text-white" /></div>,
    description: "Through our partner network, we connect you with emergency, transitional, and permanent housing options. We also provide assistance with basic needs to create stability for long-term success.",
    features: [
      "Emergency housing referrals and placement assistance",
      "Transitional housing program partnerships",
      "Housing application and documentation support",
      "Rental assistance and utilities support referrals",
      "Life skills and independent living coaching"
    ],
    color: "bg-primary-50"
  },
  {
    id: "recovery-mental-health",
    title: "Recovery & Mental Health Navigation",
    icon: <div className="p-3 rounded-full bg-gradient-to-r from-pink-400 to-fuchsia-600 shadow-md"><HeartIcon className="h-8 w-8 text-white" /></div>,
    description: "We connect you with mental health resources, substance abuse treatment, and recovery support groups. Our holistic approach ensures that your wellbeing is prioritized alongside employment goals.",
    features: [
      "Mental health assessment and referrals",
      "Substance abuse recovery program connections",
      "Support group facilitation and referrals",
      "Trauma-informed care and services",
      "Stress management and coping skills coaching"
    ],
    color: "bg-white"
  }
];

const additionalServices = [
  {
    title: "Transportation Assistance",
    icon: <div className="p-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 shadow-md"><AwardIcon className="h-5 w-5 text-white" /></div>,
    description: "We help with bus passes, rideshare vouchers, and transportation planning to ensure you can get to work reliably."
  },
  {
    title: "Legal Aid Referrals",
    icon: <div className="p-2 rounded-full bg-gradient-to-r from-blue-400 to-indigo-600 shadow-md"><HelpCircleIcon className="h-5 w-5 text-white" /></div>,
    description: "We connect you with legal resources for record expungement, child support issues, and other legal barriers."
  },
  {
    title: "Computer Lab Access",
    icon: <div className="p-2 rounded-full bg-gradient-to-r from-green-400 to-emerald-600 shadow-md"><MonitorIcon className="h-5 w-5 text-white" /></div>,
    description: "Use our on-site computer lab for job searches, resume building, and online learning."
  },
  {
    title: "Financial Literacy",
    icon: <div className="p-2 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 shadow-md"><CalendarIcon className="h-5 w-5 text-white" /></div>,
    description: "Learn budgeting, banking, credit building, and other financial skills essential for independence."
  }
];

export default function ProgramsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-white pt-12 pb-8 px-4 sm:px-6 lg:px-8 border-b">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl font-extrabold text-primary-700 sm:text-5xl md:text-6xl">Our Programs</h1>
            <p className="mt-3 text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive support services designed to address the whole person and create pathways to success.
            </p>
          </div>
        </section>

        {/* Programs */}
        <section className="pt-4 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Comprehensive Support Services</h2>
              <p className="mt-2 text-lg text-slate-600 max-w-3xl mx-auto">
                Our programs address multiple barriers simultaneously, providing holistic support on your journey to stability and success.
              </p>
            </div>

            <div className="space-y-8">
              {programs.map((program, index) => (
                <div key={program.id} id={program.id} className={`${program.color} rounded-lg shadow-md overflow-hidden`}>
                  <div className="p-6 md:p-8">
                    <div className="md:flex items-start">
                      <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
                        {program.icon}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-4">{program.title}</h3>
                        <p className="text-lg text-slate-700 mb-6">{program.description}</p>
                        <h4 className="text-lg font-semibold text-slate-800 mb-4">What We Offer:</h4>
                        <ul className="space-y-2">
                          {program.features.map((feature, i) => (
                            <li key={i} className="flex items-start">
                              <div className="flex-shrink-0 h-5 w-5 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full flex items-center justify-center mr-2 mt-0.5 shadow-sm">
                                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <span className="text-slate-600">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Services */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Additional Services</h2>
              <p className="mt-4 text-lg text-slate-600 max-w-3xl mx-auto">
                Beyond our core programs, we offer these supplementary services to address specific needs.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {additionalServices.map((service, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center mb-4">
                    {service.icon}
                    <h3 className="ml-3 text-lg font-semibold text-slate-900">{service.title}</h3>
                  </div>
                  <p className="text-slate-600">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How to Enroll */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="bg-primary-50 rounded-lg shadow-md p-8 md:p-10">
              <div className="md:flex items-center justify-between">
                <div className="mb-6 md:mb-0 md:mr-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Ready to Get Started?</h2>
                  <p className="text-lg text-slate-700">
                    Our programs are available to individuals facing barriers to employment. The first step is to complete our intake form or contact us for an initial assessment.
                  </p>
                </div>
                <div className="flex flex-col space-y-4">
                  <Link href="/get-help">
                    <Button size="lg" className="w-full">Complete Intake Form</Button>
                  </Link>
                  <Link href="/contact">
                    <Button variant="outline" size="lg" className="w-full">Contact Us</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
